import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";

import selectionsStore from "src/stores/selections";
import betBuilderStore from "src/stores/betBuilder";
import { ISelection } from "src/constants/interfaces/bet";
import {
  getBetBuilderOdds,
  IBetBuilderOddsSelection,
} from "src/services/betBuilder";
import { usePrevious } from "./previous";
import { toastWrapper } from "src/common/components/toastWrapper";

// Correct Score selections carry the score in the name (e.g. "1:1", "2-0").
// `home_value`/`away_value` aren't always populated on the event yet when bet
// builder validation fires — swarm's subscription that hydrates those fields
// can land after this call goes out. Parse the name as a fallback so we don't
// send "0:0" to the backend for every Correct Score pick.
const parseScoreFromName = (name?: string): { home: number; away: number } | null => {
  if (!name) return null;
  const match = name.trim().match(/^(\d+)\s*[:\-]\s*(\d+)$/);
  if (!match) return null;
  return { home: Number(match[1]), away: Number(match[2]) };
};

const toOddsPayload = (selection: ISelection): IBetBuilderOddsSelection => {
  const fallback = parseScoreFromName(selection.event.name);
  // `event.type` is the canonical identifier (P1/X/P2/Over/Under/...) but
  // some swarm records leave it empty — fall back through `type_1`
  // (BB-style naming on the swarm event) and `name` so `get_bet_builder_odds`
  // always carries a non-empty SelectionName. (`Kind` lives on the v2
  // markets response — not on the swarm event — so it's not a valid
  // source here.)
  const ev = selection.event as typeof selection.event & { type_1?: string };

  const selectionName = ev.type || ev.type_1 || ev.name || '';
  return {
    selectionId: selection.event.id,
    homeValue: selection.event.home_value ?? fallback?.home ?? 0,
    awayValue: selection.event.away_value ?? fallback?.away ?? 0,
    selectionTypeId: selection.event.type_id,
    marketId: selection.market.id,
    selectionName,
    price: selection.event.price,
  };
};

/**
 * Watches bet builder selections in the betslip and keeps Recoil's
 * betBuilderStore in sync with the server by calling get_bet_builder_odds.
 *
 * The server response tells us:
 *  - OddsPerMatch: combined odd per game
 *  - RejectedIds : selections that could not be combined — we remove them
 *  - TotalOdd    : aggregate odd across all bet builder groups
 *
 * Rejected selections are removed via onSelectionsChange, which flows back to
 * sportsbook's shared SelectionsContext and automatically unhighlights the
 * rejected events on the markets view.
 */
export const useBetBuilderValidation = (
  onSelectionsChange: (selections: ISelection[]) => void
) => {
  const { selections } = useRecoilValue(selectionsStore);
  const [state, setState] = useRecoilState(betBuilderStore);

  // Legs flagged `inactive` were rejected by a previous get_bet_builder_odds
  // or do_bet_terminal call and stay in the slip purely as a "deleted
  // events" marker. They MUST be excluded from subsequent odds requests so
  // the server doesn't keep returning the same RejectedIds and we can
  // settle on a valid combined odd for the rest of the card.
  const betBuilderSelections = selections.filter(
    (s) => s.event.isInternalBetBuilder && !s.inactive,
  );
  const betBuilderIds = betBuilderSelections.map((s) => s.event.id).sort();
  const prevIds = usePrevious(betBuilderIds);

  useEffect(() => {
    // No bet builder selections — reset state.
    if (!betBuilderSelections.length) {
      if (state.totalOdd !== 0 || Object.keys(state.groups).length) {
        setState({ groups: {}, totalOdd: 0, rejectedIds: [] });
      }
      return;
    }

    // Skip if the id list hasn't actually changed.
    if (prevIds && _.isEqual(prevIds, betBuilderIds)) return;

    const payload = betBuilderSelections.map(toOddsPayload);

    // Mark loading per current game group so the UI can show a spinner.
    const gameIdsInPlay = new Set(betBuilderSelections.map((s) => s.game.id));
    setState((prev) => ({
      ...prev,
      groups: Object.fromEntries(
        Array.from(gameIdsInPlay).map((gameId) => [
          gameId,
          {
            combinedPrice: prev.groups[gameId]?.combinedPrice ?? 0,
            selectionIds: betBuilderSelections
              .filter((s) => s.game.id === gameId)
              .map((s) => s.event.id),
            loading: true,
          },
        ])
      ),
    }));

    getBetBuilderOdds(payload)
      .then((details) => {
        // Remove any server-rejected selections from the betslip.
        if (details.RejectedIds.length) {
          const rejectedSet = new Set(details.RejectedIds);
          const filtered = selections.filter((s) => !rejectedSet.has(s.event.id));
          onSelectionsChange(filtered);
          toastWrapper(
            "error",
            details.RejectedIds.length === 1
              ? "This selection cannot be combined with the current bet builder"
              : "Some selections cannot be combined with the current bet builder"
          );
        }

        // Build per-game combined odds from OddsPerMatch.
        const perGame: Record<number, number> = Object.fromEntries(
          details.OddsPerMatch.map((item) => [item.MatchId, item.Odd])
        );

        setState({
          totalOdd: details.TotalOdd,
          rejectedIds: details.RejectedIds,
          groups: Object.fromEntries(
            Array.from(gameIdsInPlay)
              .filter((gameId) => !Object.keys(perGame).length || perGame[gameId] !== undefined)
              .map((gameId) => [
                gameId,
                {
                  combinedPrice: perGame[gameId] ?? 0,
                  selectionIds: betBuilderSelections
                    .filter((s) => s.game.id === gameId && !details.RejectedIds.includes(s.event.id))
                    .map((s) => s.event.id),
                  loading: false,
                },
              ])
          ),
        });
      })
      .catch(() => {
        toastWrapper("error", "Bet builder service unavailable");
        setState((prev) => ({
          ...prev,
          groups: Object.fromEntries(
            Object.entries(prev.groups).map(([gameId, group]) => [
              gameId,
              { ...group, loading: false },
            ])
          ),
        }));
      });
  }, [betBuilderIds.join(","), selections]);
};
